import type { LevelDef } from './types';

/**
 * Day 6 —Monday. The railway: a level crossing that shuts in front of her
 * (she has to stand and wait while he closes in), then the elevated line with
 * two underpasses — one pitch dark, one with a dying tube. A photo arrives.
 */
export const night5: LevelDef = {
  id: 'night5',
  night: 6,
  title: 'The Crossing',
  subtitle: 'Monday · 23:52',
  look: 'school',
  card: ['Your usual line is suspended. Replacement bus, then on foot.', 'Home is on the other side of the tracks.', 'Mom says the police "will look into it".'],
  outfit: 'uniformWinter',
  clockStart: 23 * 60 + 52,
  map: [
    '#########################', // 0
    '#############H###########', // 1
    '#############.###########', // 2
    '######....B...###########', // 3
    '######.###########.######', // 4
    '######.###########.A#####', // 5
    '######L###########L######', // 6
    '######.###########.######', // 7
    '######.###########.######', // 8
    '######.###########.######', // 9
    '####..........L......####', // 10
    'eeeeeeueeeeeeeeeeeUeeeeee', // 11
    'eeeeeeueeeeeeeeeeeueeeeee', // 12
    '####......L..........####', // 13
    '####.###############.####', // 14
    '####.###############.####', // 15
    '####.###############.####', // 16
    '####.###############.####', // 17
    '####....V.......T...F####', // 18
    '####L#######.#######.####', // 19
    '####.#######.#######.####', // 20
    '####.#######.#######.####', // 21
    '####.#######.#######.####', // 22
    '####.#######.#######.####', // 23
    '####........L........####', // 24
    'rrrrrrrrrrrrKrrrrrrrrrrrr', // 25
    '###...L.....L.........###', // 26
    '###.############.####.###', // 27
    '###.############.####.###', // 28
    '###.############.####.###', // 29
    '###.############.####V###', // 30
    '###F############.####.###', // 31
    '###.############.####.###', // 32
    '###.############.####.###', // 33
    '###.....V.......L.....###', // 34
    '###.########.########.###', // 35
    '###.########.########L###', // 36
    '###L########.########.###', // 37
    '###.########L########.###', // 38
    '###.########.########.###', // 39
    '###.########.########.###', // 40
    '###.########.########.###', // 41
    '###....L.........L....###', // 42
    '############.############', // 43
    '############V############', // 44
    '############.############', // 45
    '############L############', // 46
    '############.############', // 47
    '############.############', // 48
    '############S############', // 49
    '############.############', // 50
    '############P############', // 51
  ],
  zones: [
    { r0: 26, zone: 1 },
    { r0: 13, r1: 25, zone: 2 },
    { r1: 12, zone: 3 },
  ],
  battery: 22,
  pace: { tension: 0.62, patience: 720 },
  theme: {
    sky: '#05070c',
    fog: { color: '#0a0d14', density: 0.034 },
    hemi: { sky: '#3d4c6a', ground: '#0b0b0c', intensity: 0.7 },
    ambient: 0.5,
    moon: 0.55,
    walls: ['#8a857c', '#7a776f', '#9a9284', '#6f6d68', '#857d70'],
  },
  stranger: { walkMax: 2.95, chaseSpeed: 3.1, gapMax: 33, spawnDelay: 5 },
  lines: {
    intro: 'Across the tracks and I\'m nearly home.',
    firstSight: 'Him. He got off the bus too.',
    crossing: 'No… the bell. The gates are coming down.',
    crossingWait: 'Come on, come on. Don\'t look back. Don\'t look back.',
    underpass: 'The underpass. I can\'t see the other end.',
    underpassLit: 'That light is going to die any second.',
    photo: 'That\'s me. That\'s the back of my coat. That\'s tonight.',
    deadend: 'Fence. Just the tracks behind it.',
    wrongBuilding: 'Not this one. Keep going.',
    homeStretch: 'My building. I can see my window.',
  },
  mom: {
    first: { node: 'start', at: 7 },
    script: {
      start: {
        text: 'Did the replacement bus come?',
        options: [
          { text: 'Yes, walking from the stop', end: 'Ok. Call me if you want.' },
          { text: 'He was on the bus', next: 'bus' },
        ],
      },
      bus: {
        text: 'The same man?? Are you sure?',
        options: [
          { text: 'Maybe not', end: 'Ok. Stay where there are people.' },
          { text: "I'm sure", next: 'sure' },
        ],
      },
      sure: {
        text: "I'm calling the station. Keep walking, don't stop anywhere.",
        options: [
          { text: 'Ok', end: "I'm here." },
          { text: "The crossing's closing", end: "Then wait where it's bright." },
        ],
      },
      photo: {
        text: 'Who sent you a photo? Your phone said something about a photo.',
        options: [
          { text: 'Nobody', end: 'Hmm. Ok.' },
          { text: 'It\'s me, from behind', next: 'behind' },
        ],
      },
      behind: {
        text: 'Turn around right now and look.',
        options: [
          { text: "There's nobody there", end: 'Keep going. Fast.' },
          { text: "He's there", end: 'GO. Go home.' },
        ],
      },
    },
  },
  onEnterCell(ctx, cell) {
    const key = `${cell.c},${cell.r}`;
    if (key === '16,27' && !ctx.said('deadend')) {
      ctx.spike(0.06);
      ctx.sayOnce('deadend');
    }
    if (key === '6,12') ctx.sayOnce('underpass');
    if (key === '18,12') ctx.sayOnce('underpassLit');
    if (cell.r === 3) ctx.sayOnce('homeStretch');
  },
  onZoneChange(ctx, zone) {
    if (zone === 2 && !ctx.said('photo')) {
      // Taken a minute ago, from behind her.
      ctx.notify('photo5', 'Unknown', '[Photo] 📷', 'normal', 6);
      ctx.sayLater('photo', 7.5);
      ctx.momLater('photo', 16);
    }
  },
};
